import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { categories, videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredVideos = activeSlug === 'all'
    ? videos
    : videos.filter((v) => v.categorySlug === activeSlug);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 向日葵视频`,
    description: currentCategory.description,
    url: `https://www.xiangrikui-video.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: v.title,
          description: v.description,
          thumbnailUrl: v.thumbnail,
        },
      })),
    },
  };

  return (
    <SEO
      title={currentCategory.name}
      description={currentCategory.description}
      keywords={`向日葵视频,${currentCategory.name},免费在线观看,高清,电影电视剧,不缓冲`}
      canonicalUrl={`https://www.xiangrikui-video.com/category/${activeSlug}`}
      schemaData={schema}
    >
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="bg-background-100">
            <div className="container-main">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="pb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
              </div>
            </div>
          </div>

          <div className="container-main py-8">
            {/* Category Switcher */}
            <div className="mb-8">
              <div className="inline-flex bg-background-200/60 rounded-full px-1 py-1 flex-wrap gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all duration-200 ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50 font-medium shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-50'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {filteredVideos.length > 0 ? (
              <>
                <p className="text-sm text-foreground-400 mb-5">
                  共 <strong className="text-foreground-700">{filteredVideos.length}</strong> 个内容
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block" />
                <p className="text-foreground-500">该分类暂无内容</p>
                <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </SEO>
  );
}